import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import {
  OrganizationRiderDetail,
  OrganizationRiderPool,
  OrganizationRiderServiceLevel,
  OrganizationService,
  OrganizationServiceLevel
} from '@uzurv/clara';
import { DateFormatConstants, isNullOrUndefined, UzurvPipesModule } from '@uzurv/core';
import moment from 'moment';
import { firstValueFrom, map } from 'rxjs';
import { MaterialModule } from '../../app/material.module';
import { RequiresPermissionsModule } from '../directives/requires-permissions';
import { OrganizationRiderPoolUtil } from '../../utils/organization-rider-pools.util';

@Component({
  selector: 'app-organization-rider-profile',
  templateUrl: './organization-rider-profile-component.html',
  styleUrls: ['./organization-rider-profile-component.scss'],
  standalone: true,
  imports: [MaterialModule, CommonModule, UzurvPipesModule, RequiresPermissionsModule]
})
export class OrganizationRiderProfileComponent implements OnChanges {
  @Input() organizationRiderDetail!: OrganizationRiderDetail;

  @Output()
  editOrganizationRiderInfo = new EventEmitter<
    [OrganizationRiderDetail, Array<OrganizationRiderServiceLevel>, Array<OrganizationServiceLevel>, Array<OrganizationRiderPool> | null]
  >();

  @Output() eligibilityToggleChanged = new EventEmitter<boolean>();

  organizationServiceLevels!: Array<OrganizationServiceLevel>;

  organizationRiderPhoneNumber!: string;

  inEligibleRiderClass!: string | null;

  organizationRiderServiceLevels!: Array<OrganizationRiderServiceLevel>;

  organizationRiderPools: Array<OrganizationRiderPool> = [];

  constructor(private organizationService: OrganizationService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['organizationRiderDetail']) {
      this.organizationRiderServiceLevels = [...this.organizationRiderDetail.organizationRiderServiceLevels];
      this.fetchOrganizationServiceLevels();
      this.fetchOrganizationRiderPools();
      this.inEligibleRiderClass = this.organizationRiderDetail.eligible ? null : 'warning-text';
    }
  }

  /**
   * Returns a string based on given organizationRiderDetail ride limit information.
   *
   * @param organizationRiderDetail
   * @returns {string}
   */
  getOrganizationRiderPoolDisplayTextViaDetail(): string {
    return OrganizationRiderPoolUtil.getOrganizationRiderPoolDisplayTextViaDetail(this.organizationRiderDetail);
  }

  formatShortDate(date: Date): string {
    return moment(date).format(DateFormatConstants.SHORT_MONTH_ORDINAL_DAY);
  }

  getFormattedDateOfBirth(dateOfBirth: Date): string | null {
    if (isNullOrUndefined(dateOfBirth)) {
      return null;
    }

    return moment(dateOfBirth).format(DateFormatConstants.DATE_OF_BIRTH);
  }

  handleEditOrganizationRiderInfo() {
    this.editOrganizationRiderInfo.emit([
      this.organizationRiderDetail,
      this.organizationRiderDetail.organizationRiderServiceLevels,
      this.organizationServiceLevels,
      this.organizationRiderPools
    ]);
  }

  handleEligibilityToggleClicked(event: any) {
    this.eligibilityToggleChanged.emit(event.checked);
  }

  /**
   * Handles display of the user's organization rider pool information for a specific organization.
   *
   * @returns {boolean}
   */
  shouldDisplayOrganizationRiderPoolInfo(): boolean {
    return OrganizationRiderPoolUtil.shouldDisplayOrganizationRiderPoolInfo(this.organizationRiderDetail, this.organizationRiderPools);
  }

  private fetchOrganizationServiceLevels(): void {
    this.organizationService
      .listOrganizationServiceLevels(this.organizationRiderDetail.organizationId)
      .pipe(
        map(osl => {
          this.organizationServiceLevels = osl;

          this.organizationServiceLevels.map(osl => {
            const existingOrganizationRiderServiceLevel = this.organizationRiderServiceLevels?.find(
              orsl => orsl.serviceLevel.code === osl.serviceLevel.code
            );

            if (isNullOrUndefined(existingOrganizationRiderServiceLevel)) {
              const organizationRiderServiceLevel = new OrganizationRiderServiceLevel();
              organizationRiderServiceLevel.serviceLevel = osl.serviceLevel;
              organizationRiderServiceLevel.enabled = osl.optional ? false : true;

              this.organizationRiderServiceLevels.push(organizationRiderServiceLevel);
            }
          });
        })
      )
      .subscribe();
  }

  /**
   * Retrieves an array of available rider pools.
   */
  private async fetchOrganizationRiderPools(): Promise<void> {
    this.organizationRiderPools = await firstValueFrom(
      this.organizationService.listRiderPoolsByOrganizationId(this.organizationRiderDetail.organizationId)
    );
  }
}
